import type { Metric } from "@unbox/api";

// Shared metrics configuration used across Reports, ReportDetail, and Analytics Dashboard
export const METRICS_MAP: Record<Metric, { label: string; description: string }> = {
  gmv: { label: "GMV", description: "Gross Merchandise Value" },
  aov: { label: "AOV", description: "Average Order Value" },
  ctor: { label: "CTOR", description: "Click-to-Order Rate" },
  videos: { label: "# Videos", description: "Number of Videos" },
  unitSales: { label: "Unit Sales", description: "Total Units Sold" },
  views: { label: "Views", description: "Total Video Views" },
  creators: { label: "Creators Posted", description: "Number of Creators who have posted" },
  totalCreators: { label: "Total Creators", description: "Total Creators who have posted or made a sale" },
  gpm: { label: "GPM", description: "GMV per 1k views" },
  gmvPerVideo: { label: "GMV per Video", description: "GMV per Video" },
  gmvPerRDV: {
    label: "GMV per Revenue Driving Video",
    description: "GMV per Revenue Driving Video",
  },
  ctr: { label: "CTR", description: "Click-Through Rate" },
  orders: { label: "Orders", description: "Total Orders" },
  rdv: { label: "Revenue Driving Videos ", description: "Revenue Driving Videos" },
  viewsPerVideo: { label: "Views per Video", description: "Average Views per Video" },
  productImpressions: { label: "Product Impressions", description: "Total Product Impressions" },
  rdc: { label: "Revenue Driving Creators", description: "Revenue Driving Creators" },
  newCreatorPosting: { label: "New Creator Posting", description: "New Creators Posting Content" },
  newCreatorVideos: { label: "New Creator Videos", description: "Videos from New Creators" },
  dormantCreators: {
    label: "Dormant Creators",
    description: "Previously Active Creators not active in the past 30 days",
  },
  creatorRetention: { label: "Creator Retention", description: "Creator Retention Rate (30 Days)" },
  creatorChurn: { label: "Creator Churn", description: "Creator Churn Rate (30 Days)" },
  gmvPerCreator: { label: "GMV per Creator", description: "GMV per Creator" },
  gmvPerRDC: { label: "GMV per Revenue Driving Creator", description: "GMV per Revenue Driving Creator" },
} as const;

export const ALL_METRICS: Metric[] = [
  "gmv",
  "aov",
  "ctor",
  "videos",
  "unitSales",
  "views",
  "creators",
  "totalCreators",
  "gpm",
  "gmvPerVideo",
  "gmvPerRDV",
  "ctr",
  "orders",
  "rdv",
  "viewsPerVideo",
  "productImpressions",
  "rdc",
  "newCreatorPosting",
  "newCreatorVideos",
  "dormantCreators",
  "creatorRetention",
  "creatorChurn",
  "gmvPerCreator",
  "gmvPerRDC",
];

export const AVAILABLE_METRICS = ALL_METRICS.map((m) => ({ value: m, ...METRICS_MAP[m] }));

export const CURRENCY_METRICS: Metric[] = [
  "gmv",
  "aov",
  "gmvPerCreator",
  "gmvPerVideo",
  "gpm",
  "gmvPerRDC",
  "gmvPerRDV",
];

export const PERCENT_METRICS: Metric[] = ["creatorChurn", "creatorRetention", "ctr", "ctor"];

// Metric color mapping for charts
export const METRIC_COLORS: Partial<Record<Metric, string>> = {
  gmv: "#3b82f6",
  videos: "#10b981",
  unitSales: "#6366f1",
  aov: "#8b5cf6",
  views: "#22c55e",
  creators: "#a855f7",
  newCreatorPosting: "#14b8a6",
  gmvPerCreator: "#f97316",
  gmvPerVideo: "#f43f5e",
  gmvPerRDC: "#06b6d4",
  viewsPerVideo: "#f59e0b",
};

// Revenue source colors (for template reports)
export const REVENUE_SOURCE_COLORS = {
  LIVE: "#2E1A47", // Dark purple (Live Streams)
  VIDEO: "#6941C6", // Medium purple (Short Video)
  PRODUCT_CARD: "#D4BCFA", // Light purple (Product Card)
};

// Creator segment colors (for Creator Community Health template)
export const CREATOR_SEGMENT_COLORS = {
  new: "#10b981", // green
  active: "#3b82f6", // blue
  reactivated: "#f59e0b", // orange
  dormant: "#ef4444", // red
};

// Helper function to get metrics as array for dropdowns and filtering
export const getMetricsArray = () => {
  return AVAILABLE_METRICS;
};

export const getFilteredMetricsArray = () => AVAILABLE_METRICS;
